import {createGroup, findChildrenByName, setBox3FromObjectArray} from "../../utils/threeUtils";
import {buildingGroup, isOrthogonalMode, mousePosition, tmpGroup} from "../../main";
import {Box3, Euler, Group, Mesh, Raycaster, Vector3} from "three";
import * as Globals from "../../globals";
import {snapPointToClosestWallBoundary} from "../../utils/snappingUtils";
import {wallDirection, wallDirectionAxis} from "../walls/wallGeneration";
import {toFixedFloat} from "../../utils/utils";
import {Text} from "../baseElements/text";
import {TransactionManager} from "../../TransactionManager";
import {Line, LineGeometry} from "../baseElements/line";
import {setObjectLayer} from "../../utils/layerUtils";
import {raehmMeshName} from "../../constants/nameConstants";

const wallSnapTolerance = 0.05;
const previewYPosition = 4;
const lineWidth = 0.0075;
const halfLineWidth = lineWidth / 2;

let raehmRaycaster = new Raycaster();
raehmRaycaster.params.Mesh.threshold = 0.1;
raehmRaycaster.layers.enable(Globals.layers.raehm);

export class RoomLinePreview {
    constructor() {
        this.allRaehmMeshes = findChildrenByName(buildingGroup, raehmMeshName, false, true);
        // this.allRaehmMeshes = this.allRaehmMeshes.concat(getRoomLines());

        this.firstPreviewMesh = null; //Erstes Vorschau Viereck. Wird beim Klick auf Wandbutton erstellt
        this.secondPreviewMesh = null; //Zweite Vorschau der zu erstellenden Wand. Wird nach Platzierung des ersten Punkts/Vorschau generiert
        this.secondPreviewCrossMesh = null; //Zweite Vorschau der zu erstellenden Wand. Wird nach Platzierung des ersten Punkts/Vorschau generiert
        this.previousDirection = null; //Richtung der zuletzt gesetzt Wand während aktueller Vorschau sofern vorhanden
        this.previousCross = null;
        this.newWallPlaced = false;
        this.group = createGroup("roomLineGroup");
        tmpGroup.addWithUuid(this.group);

        this.createFirstPreview(); //Erzeugen der ersten Vorschau
    }

    //First preview step
    createFirstPreview() {
        this.firstPreviewMesh = this.createFirstPreviewMesh();
        this.adjustFirstPreview();
        tmpGroup.addWithUuid(this.firstPreviewMesh);
    }

    adjustFirstPreview() {
        let snapPoint = this.snapFirstPreview();
        if (snapPoint === null) {
            snapPoint = mousePosition;
        }
        this.firstPreviewMesh.position.copy(snapPoint);
        this.firstPreviewMesh.position.y = previewYPosition;
    }

    createFirstPreviewMesh() {
        //Generiere Vorschau für ersten Wandklick
        let lineMesh1 = new Line(new Vector3(-0.05, 0, 0), new Vector3(0.05, 0, 0), lineWidth);
        let lineMesh2 = new Line(new Vector3(-0.05, 0, 0), new Vector3(0.05, 0, 0), lineWidth);
        lineMesh1.rotateY(Math.PI / 4);
        lineMesh2.rotateY(-Math.PI / 4);
        let group = createGroup("roomLineCross");
        group.addWithUuid(lineMesh1);
        group.addWithUuid(lineMesh2);
        setObjectLayer(group, Globals.layers.layoutViewOnly, true);
        return group;
    }

    snapFirstPreview() {
        //TODO: Wall boundary am start/ende der wand kann teilweise nicht gesnapped werden wenn andere Wände angrenzen. Bounds von denen liegen wahrscheinlich näher dann
        //Snap an bestehende Wände probieren
        let wallPosition = snapPointToClosestWallBoundary(mousePosition, this.allRaehmMeshes, 0);
        if (wallPosition !== null) {
            if (wallPosition.distanceTo(mousePosition) <= wallSnapTolerance) {
                return wallPosition;
            }
        }

        return null; //Wenn weder noch zutrifft null zurückgeben. Kein Snappoint konnte berechnet werden. Wird anschließend an Mausposition gesetzt
    }

    //Functions for second preview step
    createSecondPreview() {
        //Aufruf aus Klick Event.
        //Entfernt die erste preview aus der Ansicht. Wird aber für spätere Berechnungen noch beibehalten.
        this.group.addWithUuid(new Group().copy(this.firstPreviewMesh, true));
        this.firstPreviewMesh.removeFromParent();
        this.adjustSecondPreview();
    }

    adjustSecondPreview() {
        //Relevante Werte vor Berechnung zurücksetzen
        this.direction = null;
        this.directionAxis = null;
        this.directionVector = null;
        this.firstPositionOverwrite = null;
        this.cancelAfterWallCreation = false;

        //Prüfen ob Richtung begrenzt werden muss: Abhängig von vorher platzierten Wänden, während der gleichen Vorschau
        let allowedDirections = [];
        if (this.previousDirection !== null) {
            //Erlaubte Richtungen festlegen
            if (this.previousDirection === wallDirection.negativeX || this.previousDirection === wallDirection.positiveX) {
                allowedDirections = [wallDirection.negativeZ, wallDirection.positiveZ];
            } else if (this.previousDirection === wallDirection.negativeZ || this.previousDirection === wallDirection.positiveZ) {
                allowedDirections = [wallDirection.negativeX, wallDirection.positiveX];
            }
        }

        //Orientierung und Länge der Wand/Wandvorschau berechnen. Basierend auf Position der ersten Vorschau und der aktuellen Mausposition. Sowie Richtungsbegrenzungen
        this.calculateDirectionVector(allowedDirections);

        //Wenne zweite Wand während einer Vorschau generiert wird:
        this.adjustFirstPreviewPosition();

        //Aktuelle Wandlänge bestimmen
        this.length = toFixedFloat(this.directionVector.length(), 5);

        //Prüfen ob an eine Wand gesnappt werden kann die näher liegt als die aktuelle Länge
        if (this.length > 0) {
            let snapPoint = this.snapToExistingWalls();

            if (snapPoint !== null) {
                this.directionVector = new Vector3().subVectors(snapPoint, this.firstPositionOverwrite);
                this.length = this.directionVector.length();
                this.cancelAfterWallCreation = true;
            }
        }

        //Generierung des eigentlichen Vorschauelements mit berechneten Werten
        this.adjustSecondPreviewMesh();
    }

    calculateDirectionVector(allowedDirections = []) {
        //Richtungsvektor berechnen
        let directionVector = new Vector3().subVectors(mousePosition, this.firstPreviewMesh.position);
        this.firstPositionOverwrite = new Vector3().copy(this.firstPreviewMesh.position); //Position der ersten Vorschau. Punkt wird anschließend abhängig von der Wandrichtung verschoben
        directionVector.setY(0); //Y Koordinate für Berechnung irrelevant > Nullen > Verfälscht sonst die Länge der Wand

        //Zunächst nur orthogonale Wände. Muss ggf. später erweitert werden
        if (isOrthogonalMode) {
            //Ausrichtung entweder nach Richtung mit größerer Distanz oder wenn Richtung vorgegeben per Array dann in diese begrenzen
            let xOriented = Math.abs(directionVector.x) > Math.abs(directionVector.z);
            let directionZAllowed = allowedDirections.includes(wallDirection.positiveZ) || allowedDirections.includes(wallDirection.negativeZ);
            let directionXAllowed = allowedDirections.includes(wallDirection.positiveX) || allowedDirections.includes(wallDirection.negativeX);

            if ((xOriented && !directionZAllowed) || directionXAllowed) {
                if (mousePosition.x > this.firstPreviewMesh.position.x) {
                    this.direction = wallDirection.positiveX;
                } else {
                    this.direction = wallDirection.negativeX;
                }
                this.directionAxis = wallDirectionAxis.xAxis;
                directionVector.setZ(0);
            } else {
                if (mousePosition.z > this.firstPreviewMesh.position.z) {
                    this.direction = wallDirection.positiveZ;
                } else {
                    this.direction = wallDirection.negativeZ;
                }
                this.directionAxis = wallDirectionAxis.zAxis;
                directionVector.setX(0);
            }
        }

        let currentLength = directionVector.length(); //Länge des Vektors
        directionVector.clampLength(currentLength, currentLength); //Verlängern um die Distanz zwischen erstem Viereck Mittelpunkt bis Bounds

        this.directionVector = directionVector;
    }

    adjustFirstPreviewPosition() {
        //Erste Vorschauposition anpassen: Snap an die 4 möglichen Position die sich auf Basis der vorherigen Wand ergeben
        if (this.firstWallPlaced) {
            let position = new Vector3().copy(this.previousEndPoint);
            let position2 = new Vector3().copy(this.previousEndPoint);
            //Aktuelle Wand in X Richtung
            if (this.directionAxis === wallDirectionAxis.xAxis) {
                //Vorherige Wand in +Z Richtung
                if (this.previousDirection === wallDirection.positiveZ) {
                    position.x += (this.direction === wallDirection.positiveX ? halfLineWidth : -halfLineWidth);
                    position.z -= halfLineWidth;
                    position2.z = position.z;
                } else if (this.previousDirection === wallDirection.negativeZ) {
                    position.x += (this.direction === wallDirection.positiveX ? halfLineWidth : -halfLineWidth);
                    position.z += halfLineWidth;
                    position2.z = position.z;
                }
            } else { //Aktuelle Wand in Z Richtung
                //Vorherige Wand in +X Richtung
                if (this.previousDirection === wallDirection.positiveX) {
                    position.z += (this.direction === wallDirection.positiveZ ? halfLineWidth : -halfLineWidth);
                    position.x -= halfLineWidth;
                    position2.x = position.x;
                } else if (this.previousDirection === wallDirection.negativeX) {
                    position.z += (this.direction === wallDirection.positiveZ ? halfLineWidth : -halfLineWidth);
                    position.x += halfLineWidth;
                    position2.x = position.x;
                }
            }

            this.firstPreviewMesh.position.copy(position);
            this.previousCross.position.copy(position2);
        }
    }

    snapToExistingWalls() {
        //Setzt vorraus das Rähme alle auf einer Höhe liegen && gleiche Höhe
        let yCenter = setBox3FromObjectArray(this.allRaehmMeshes).getCenter(new Vector3()).y; //Mittlere y Werte der Rähme
        let direction = new Vector3().copy(this.directionVector).clampLength(1, 1);  //Normalisierte Richtung der Wand für Raycaster
        let originMin, originMax;
        let firstPreviewBounds = new Box3().setFromObject(this.firstPreviewMesh);

        //Linke und Rechte Bounds der Wand in Laufrichtung
        if (this.directionAxis === wallDirectionAxis.xAxis) {
            originMin = new Vector3(this.firstPreviewMesh.position.x, yCenter, firstPreviewBounds.min.z);
            originMax = new Vector3(this.firstPreviewMesh.position.x, yCenter, firstPreviewBounds.max.z);
        } else {
            originMin = new Vector3(firstPreviewBounds.min.x, yCenter, this.firstPreviewMesh.position.z);
            originMax = new Vector3(firstPreviewBounds.max.x, yCenter, this.firstPreviewMesh.position.z);
        }

        //Intersection mit Rähmen bestimmen für beide Boundpunkte
        raehmRaycaster.set(originMin, direction);
        let intersectionsMin = raehmRaycaster.intersectObjects(this.allRaehmMeshes, false); //Schnittpunkte Richtung 1 mit den Wänden
        raehmRaycaster.set(originMax, direction);
        let intersectionsMax = raehmRaycaster.intersectObjects(this.allRaehmMeshes, false); //Schnittpunkte Richtung 1 mit den Wänden

        //Wenn intersection gefunden: Prüfen ob die Länge bis zum Punkt kürzer wäre als die aktuelle Wandlänge > Relevanter Snappoint. Sonst null zurückgeben
        let minSnapPoint = null;
        let minSnapDistanceTo = null;
        if (intersectionsMin.length > 0) {
            let snapPoint = new Vector3(intersectionsMin[0].point.x, originMin.y, intersectionsMin[0].point.z);
            let distance = originMin.distanceTo(snapPoint);
            if (distance < this.length) {
                minSnapPoint = snapPoint;
                minSnapDistanceTo = distance;
            }
        }

        let maxSnapPoint = null;
        let maxSnapDistanceTo = null;
        if (intersectionsMax.length > 0) {
            let snapPoint = new Vector3(intersectionsMax[0].point.x, originMax.y, intersectionsMax[0].point.z);
            let distance = originMax.distanceTo(snapPoint);
            if (distance < this.length) {
                maxSnapPoint = snapPoint;
                maxSnapDistanceTo = distance;
            }
        }

        let snapPoint = null;
        //Snappoints prüfen. Wenn beide gefunden > Nächsten nehmen. Wenn nur einer diesen. Sonst keinen Snappunkt zurückgeben
        if (maxSnapPoint !== null && minSnapPoint !== null) {
            snapPoint = minSnapDistanceTo < maxSnapDistanceTo ? minSnapPoint : maxSnapPoint;
        } else if (maxSnapPoint !== null) {
            snapPoint = maxSnapPoint;
        } else if (minSnapPoint !== null) {
            snapPoint = minSnapPoint;
        }

        if (snapPoint !== null) {
            snapPoint.y = previewYPosition;
            if (this.directionAxis === wallDirectionAxis.xAxis) {
                snapPoint.z = this.firstPreviewMesh.position.z;
            } else {
                snapPoint.x = this.firstPreviewMesh.position.x;
            }
            return snapPoint;
        }

        return null;
    }

    adjustSecondPreviewMesh() {
        //Position des zweiten Vorschau Meshes
        let position = new Vector3().copy(this.firstPositionOverwrite).add(new Vector3().copy(this.directionVector).multiplyScalar(0.5));
        position.y = previewYPosition;

        //Neuen Mesh generieren und der Szene hinzufügen, falls erste mal aufgerufen. Sonst nur Geometrie aktualisieren > Performancegründe
        if (this.secondPreviewMesh === null) {
            this.secondPreviewMesh = this.createSecondPreviewMesh();
            this.secondPreviewCrossMesh = this.createFirstPreviewMesh();
            this.measurementText = this.createMeasurementText();
            tmpGroup.addWithUuid(this.secondPreviewMesh);
            tmpGroup.addWithUuid(this.secondPreviewCrossMesh);
            tmpGroup.addWithUuid(this.measurementText);
        } else {
            if (this.newWallPlaced) {
                tmpGroup.addWithUuid(this.secondPreviewMesh);
                tmpGroup.addWithUuid(this.measurementText);
                this.newWallPlaced = false;
            }
            let endPosition = new Vector3().copy(this.firstPositionOverwrite).add(this.directionVector);
            let geometry = new LineGeometry(this.firstPreviewMesh.position, endPosition, lineWidth);
            this.secondPreviewMesh.geometry.dispose();
            this.secondPreviewMesh.geometry = geometry;
            this.secondPreviewCrossMesh.position.copy(endPosition);
            this.secondPreviewCrossMesh.position.y = previewYPosition;
            this.measurementText = this.createMeasurementText(this.measurementText);
        }

        //Rotation der Vorschau basierend auf Wandrichtung einstellen
        if (this.direction === wallDirection.positiveZ) {
            this.secondPreviewMesh.rotation.y = -Math.PI / 2;
        } else if (this.direction === wallDirection.negativeZ) {
            this.secondPreviewMesh.rotation.y = Math.PI / 2;
        } else {
            this.secondPreviewMesh.rotation.y = 0;
        }

        //Position übernehmen
        this.secondPreviewMesh.position.copy(position);
    }

    createSecondPreviewMesh() {
        //Generiere Vorschau für Linie
        let previewMesh = new Line(new Vector3(0, 0, 0), new Vector3(this.length, 0, 0), lineWidth);
        previewMesh.name = "roomLineMesh";
        setObjectLayer(previewMesh, Globals.layers.layoutViewOnly, true);
        return previewMesh;
    }

    //Functions for creating the wall
    createRoomLineFromPreview() {
        if (this.length < 0.1) {
            return true;
        }
        //Erstellung der eigentlich Wand basierend auf der Vorschau
        TransactionManager.startTransaction();

        this.secondPreviewMesh.removeFromParent();
        this.measurementText.removeFromParent();
        // addBoundingBoxToLine(this.secondPreviewMesh, this.firstPreviewMesh.position, new Vector3().copy(this.firstPositionOverwrite).add(this.directionVector), lineWidth);

        this.previousCross = new Group().copy(this.secondPreviewCrossMesh, true);
        this.group.addWithUuid(this.previousCross);
        this.group.addWithUuid(new Mesh().copy(this.secondPreviewMesh)); //Hinzufügen

        //Abbruchbedingung wenn Wand in einem Snappoint an einer Wand endet
        if (this.cancelAfterWallCreation) {
            this.group.removeFromParent();
            buildingGroup.addWithUuid(this.group);
            // clearRoomLineState();
            TransactionManager.stopTransaction();
            return false;
        }

        TransactionManager.stopTransaction();

        this.allRaehmMeshes = findChildrenByName(buildingGroup, raehmMeshName, false, true);
        // this.allRaehmMeshes = this.allRaehmMeshes.concat(getRoomLines());

        this.firstWallPlaced = true;
        this.newWallPlaced = true;

        //Position der ersten Vorschau updaten an das Ende der angelegten Wand. Für Berechnung der nächsten Wandvorschau
        let tmpLength = this.directionVector.length();
        let tmpDirectionVector = new Vector3().copy(this.directionVector).clampLength(tmpLength, tmpLength);
        let newPreviewPosition = new Vector3().copy(this.firstPositionOverwrite).add(tmpDirectionVector);
        this.firstPreviewMesh.position.copy(newPreviewPosition);
        this.previousDirection = this.direction;
        this.previousEndPoint = new Vector3().copy(this.firstPositionOverwrite).add(this.directionVector);

        return true;
    }

    //Utils
    createMeasurementText(measurementText = null) {
        let bounds = new Box3().setFromObject(this.secondPreviewMesh);
        let text = "0.000";
        if (bounds !== undefined) {
            let size = bounds.getSize(new Vector3());
            text = Math.abs(this.directionVector.x) > Math.abs(this.directionVector.z) ? toFixedFloat(size.x) : toFixedFloat(size.z);
            text = text.toFixed(Globals.roundToDecimals);
        }

        if (measurementText === null) {
            measurementText = new Text(text, 0.2, 'center', -0.25, new Vector3(0, 0, 0), new Euler(-Math.PI / 2, 0, 0), Globals.layers.layoutViewOnly);
            measurementText.color = 0x000000;
        } else {
            measurementText.text = text;
        }

        //Position measurement text
        measurementText.position.x = this.secondPreviewMesh.position.x;
        measurementText.position.y = previewYPosition;
        measurementText.position.z = this.secondPreviewMesh.position.z;
        measurementText.rotation.z = this.secondPreviewMesh.rotation.y === 0 ? 0 : Math.PI / 2;
        measurementText.sync();

        return measurementText;
    }
}
